import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ModerationTransitions, ModerationStatuses } from '../../../../../models';

import { colors } from '../../../../../constants/moderation';
import { handleSearchQuery } from '../../../../manager/components/context';
import { useCurrentPageFilters } from '../../../../../utils/filters';
import { capitalizeFirstLetter } from '../../../../attendee/detail/components/utils';

import { Card, ContentCard, Dropdown, DropdownItem, Filter, FilterSetController, Icon, Status, StopPropagation, Tab, Table, ToastContext, TooltipHelp } from '@weezevent/nacre';
import ConfirmModal from '../../../../../components/confirmModal';
import LoadingTable from '../../../../../components/loadingTable';
import ModerationPaginator from '../../../components/moderationPaginator';

const spec = {
    multiple: ['transition_type', 'source_status__in', 'destination_status__in'],
    fields: ['search'],
    aliases: {},
    date: [],
    form: []
};

export default function TransitionsTab({ organization, flowId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const prefix = React.useMemo(() => `/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions`, [flowId, organization]);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [transitionToDelete, setTransitionToDelete] = React.useState(false);
    const [pageSize] = React.useState(50);
    const [filters, setFilters] = useCurrentPageFilters(spec);
    const defaultFilters = {};

    const [transitions, loadingTransitions, syncTransitions] = ModerationTransitions.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            cache: false,
            allow_cache: false,
            query: {
                limit: pageSize,
                offset: currentPage * pageSize,
                ...handleSearchQuery(spec, filters)
            }
        },
        [organization, flowId, filters, currentPage, handleSearchQuery]
    );

    const [statuses, loadingStatuses] = ModerationStatuses.useApiModel(
        {
            organizationId: organization.id,
            flowId
        },
        [organization, flowId]
    );

    const handleSearch = React.useCallback(
        value => {
            setFilters({ ...filters, search: value });
        },
        [filters]
    );

    const onDelete = React.useCallback(
        transition => {
            transition
                .delete({ organizationId: organization.id, flowId })
                .then(() => {
                    toast.success(t(`ticket.moderation.statusesTransitions.transitionsTable.actionDelete.toastSuccess`));
                    setTransitionToDelete(false);
                    syncTransitions();
                })
                .catch(() => {
                    toast.error(t(`ticket.moderation.statusesTransitions.transitionsTable.actionDelete.toastError`));
                });
        },
        [organization, flowId, syncTransitions]
    );

    const statusFilterOptions = React.useCallback(() => {
        if (!statuses || loadingStatuses) {
            return [];
        }
        return statuses.map(status => status.toOptions(flowId));
    }, [statuses, loadingStatuses, flowId]);

    return (
        <Tab
            panes={[
                {
                    id: 0,
                    menuItem: t('ticket.moderation.statusesTransitions.statusTab'),
                    menuIcon: 'check-circle',
                    url: `${prefix}/statuses`
                },
                {
                    id: 1,
                    menuItem: t('ticket.moderation.statusesTransitions.transitionsTab'),
                    menuIcon: 'transfer',
                    url: `${prefix}/transitions`
                }
            ]}
            activeTab={1}
            onTabSelected={tab => history.push(tab.url)}
        >
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={transitions?.length || 0}
                        labelTotal={t('ticket.moderation.statusesTransitions.transitionsTable.countTotal', { count: transitions?.length || 0 })}
                        onChange={values => setFilters(values)}
                        initialValues={filters}
                        defaultValues={defaultFilters}
                        onReset={() => {}}
                        search={{
                            onChange: (_, search = '') => handleSearch(search),
                            handleReset: () => setFilters(filters => ({ ...filters, search: '' })),
                            placeholder: t('ticket.moderation.common.searchPlaceholder'),
                            value: filters?.search
                        }}
                    >
                        <Filter
                            index={0}
                            multiple
                            name="source_status__in"
                            options={statusFilterOptions()}
                            labelName={t('ticket.moderation.statusesTransitions.transitionsTable.startStatus')}
                        />
                        <Filter
                            index={1}
                            multiple
                            name="destination_status__in"
                            options={statusFilterOptions()}
                            labelName={t('ticket.moderation.statusesTransitions.transitionsTable.endStatus')}
                        />
                    </FilterSetController>
                </ContentCard>
                <Table useNew>
                    <Table.Header>
                        <Table.Tr>
                            <th>{t('ticket.moderation.statusesTransitions.transitionsTable.name')}</th>
                            <th>{t('ticket.moderation.statusesTransitions.transitionsTable.startStatus')}</th>
                            <th>{t('ticket.moderation.statusesTransitions.transitionsTable.endStatus')}</th>
                            <th>
                                {t('ticket.moderation.statusesTransitions.transitionsTable.transitionAction')}
                                <TooltipHelp title={t('ticket.moderation.statusesTransitions.transitionsTable.tooltip.transitionAction')} />
                            </th>
                            <th>{t('ticket.moderation.statusesTransitions.transitionsTable.actions')}</th>
                        </Table.Tr>
                    </Table.Header>
                    {!!transitions?.length && !loadingTransitions && (
                        <Table.TBody>
                            {transitions?.map(transition => (
                                <TransitionsRow
                                    key={transition.id}
                                    transition={transition}
                                    organization={organization}
                                    flowId={flowId}
                                    setTransitionToDelete={setTransitionToDelete}
                                />
                            ))}
                        </Table.TBody>
                    )}
                </Table>
                <LoadingTable loading={loadingTransitions} height="30vh" />
                {!loadingTransitions && !transitions?.length && (
                    <Card.EmptyState
                        title={t('ticket.moderation.statusesTransitions.transitionsTable.emptyTitle')}
                        icon={<Icon name="list" />}
                        subtitle={t('ticket.moderation.statusesTransitions.transitionsTable.emptySubtitle')}
                        labelButton={t('ticket.moderation.statusesTransitions.transitionsTable.emptyCta')}
                    />
                )}
                {transitionToDelete && (
                    <ConfirmModal
                        labelTitle={t('ticket.moderation.statusesTransitions.transitionsTable.actionDelete.modalTitle')}
                        labelBody={t('ticket.moderation.statusesTransitions.transitionsTable.actionDelete.modalDescription')}
                        objectToSave={transitionToDelete}
                        onClose={() => setTransitionToDelete(false)}
                        onAccept={onDelete}
                    />
                )}
                <ModerationPaginator
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalCount={+transitions?.__meta?.headers?.get('total-count') || 0}
                    pageSize={pageSize}
                />
            </Card>
        </Tab>
    );
}

const TransitionsRow = ({ transition, organization, flowId, setTransitionToDelete }) => {
    const { t } = useTranslation();
    const history = useHistory();

    return (
        <Table.Tr onClick={() => history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/transitions/${transition.id}`)}>
            <Table.Td>{capitalizeFirstLetter(transition.name)}</Table.Td>
            <Table.Td>
                {!transition.source_status ? (
                    <span>{t('ticket.moderation.statusesTransitions.transitionsTable.allStartStatus')}</span>
                ) : (
                    <Status color={colors[transition.source_status.stage]} text={transition.source_status.name} />
                )}
            </Table.Td>
            <Table.Td>
                <Status color={colors[transition.destination_status.stage]} text={transition.destination_status.name} />
            </Table.Td>
            <Table.Td>{transition.actions.length}</Table.Td>
            <Table.Td>
                <Dropdown closeOnDocumentClick size="medium">
                    <StopPropagation>
                        <DropdownItem
                            item={t('ticket.moderation.statusesTransitions.transitionsTable.actionEdit')}
                            onClick={() => history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/transitions/${transition.id}`)}
                        />
                        <DropdownItem item={t('ticket.moderation.statusesTransitions.transitionsTable.actionSeeOperators')} />
                        <DropdownItem
                            deleteItem
                            onClick={() => setTransitionToDelete(transition)}
                            item={t('ticket.moderation.statusesTransitions.transitionsTable.actionDelete.title')}
                        />
                    </StopPropagation>
                </Dropdown>
            </Table.Td>
        </Table.Tr>
    );
};
